const en = {
    common: {
        previousPage: "Previous page",
        nextPage: "Next page",
        unknownUser: "Unknown user",
        cancel: "Cancel",
        close: "Close",
        loading: "Loading...",
        required: "Required",
        units: {
            ml: "ml",
            min: "min",
        },
    },
    app: {
        updateAvailable: "A new version of Saufdings is available.",
        reload: "Reload",
        theme: {
            dark: "Dark",
            light: "Light",
            label: "Theme",
        },
    },
    eventStats: {
        authRequired: {
            title: "Event statistics",
            description: "Please sign in to explore event leaderboards and detailed statistics.",
        },
        header: {
            title: "Event statistics",
            subtitle: "Dive into who drank what, how much pure alcohol was consumed, and how the event evolved.",
            selectPlaceholder: "Select event",
            optionNoStart: "No start time",
            startedLabel: "Started {{time}}",
            errorTitle: "Unable to load statistics",
            retryLabel: "Retry loading statistics",
        },
        visibilityOptions: {
            public: "Public",
            friends: "Friends",
            password: "Password protected",
            private: "Private",
            unknown: "Unknown",
            passwordSuffix: " (password)",
        },
        visibilityCard: {
            title: "Visibility for ended event",
            currentLabel: "Current visibility",
            description: "This event is over, but you can still switch who can see its stats.",
            form: {
                visibilityLabel: "Visibility",
                visibilityRequired: "Select a visibility option.",
                passwordLabel: "Access password",
                passwordRequired: "Enter a password for the event.",
                passwordPlaceholder: "Enter phrase",
                submit: "Update visibility",
            },
            deleteConfirmTitle: "Delete this event?",
            deleteConfirmDescription: "All stats, drinks, and related tournaments will be removed permanently.",
            deleteConfirmOk: "Delete",
            deleteButton: "Delete event",
        },
        messages: {
            visibilityUpdated: "Event visibility updated.",
            visibilityUpdateError: "Unable to update event visibility.",
            deleteSuccess: "Event deleted.",
            deleteError: "Unable to delete event.",
            loadStatsError: "Unable to load event statistics.",
            loadEventsError: "Unable to load events.",
        },
        empty: {
            selectPrompt: "Select an event to see the stats",
        },
        summary: {
            label: "Event",
            started: "Started {{time}}",
            ended: " · Ended {{time}}",
            startUnknown: "Start time unknown",
            stats: {
                visibleParticipants: "Visible participants",
                totalEntries: "Total entries",
                pureAlcohol: "Pure alcohol",
                totalLiquid: "Total liquid",
            },
        },
        filters: {
            title: "Filters",
            drinkLabel: "Drink filter",
            drinkPlaceholder: "All drinks",
            participantsLabel: "Visible participants",
            participantsPlaceholder: "Select participants",
            showAll: "Show all",
            hideAll: "Hide all",
        },
        cards: {
            topAlcoholTitle: "Most pure alcohol this event",
            topAlcoholEntry: "{{alcohol}} · {{volume}} · {{entries}} entries",
            selectedDrinkTitle: "Leaderboard for {{drink}}",
            participantTitle: "Participant leaderboard",
            categoryBreakdown: "Category breakdown",
            drinkBreakdown: "Drink breakdown",
            topDrinksTitle: "Top drinks",
            topCategoriesTitle: "Top categories",
            timelineLiquidTitle: "Timeline (liquid per hour)",
            timelineLiquidEmpty: "No timeline data for this event yet.",
            timelineAlcoholTitle: "Timeline (pure alcohol per hour)",
            timelineAlcoholEmpty: "No alcohol timeline data yet.",
            timelineTooltipAlcohol: "Pure alcohol",
            totalLabel: "Total: {{volume}} · {{alcohol}}",
            topBeerTitle: "Top beer fans",
            beerFallback: "No beer-specific data available – showing the top overall entry for context.",
            topShotsTitle: "Top shots & spirits",
            shotsFallback: "No shots or spirits data found – showing the next best entry overall.",
            topDrinkersLabel: "Top drinkers",
            noTopConsumer: "No top consumer yet.",
            noBeerData: "No beer activity tracked yet.",
            noShotsData: "No shots or spirits tracked yet.",
        },
        tables: {
            expandColumn: "Expand participant details",
            participantsEmpty: "No participants match the current filters.",
            drinkEmpty: "No drinks tracked for this event yet.",
            categoryEmpty: "No category data available.",
            selectedDrinkColumns: {
                participant: "Participant",
                entries: "Entries",
                liquid: "Liquid",
                alcohol: "Pure alcohol",
            },
            participantColumns: {
                participant: "Participant",
                details: "Details",
                entries: "Entries",
                liquid: "Liquid",
                alcohol: "Pure alcohol",
                selectedDrinkVolume: "{{drink}} (ml)",
                selectedDrinkAlcohol: "{{drink}} (g)",
            },
            drinkColumns: {
                drink: "Drink",
                entries: "Entries",
                liquid: "Total liquid",
                alcohol: "Pure alcohol",
                topConsumer: "Top consumer",
            },
            categoryColumns: {
                category: "Category",
                entries: "Entries",
                liquid: "Total liquid",
                alcohol: "Pure alcohol",
                topConsumer: "Top consumer",
            },
        },
        ongoing: {
            title: "Ongoing events leaderboard",
            started: "Started {{time}}",
            startUnknown: "Start time unknown",
            noDrinks: "No drinks recorded yet.",
            empty: "No active events with statistics yet.",
        },
    },
    profile: {
        push: {
            title: "Drink reminder",
            description: "Show a persistent, private reminder after a recent drink. Notifications contain no drink details and may arrive late or not at all.",
            loading: "Checking reminder settings",
            switchLabel: "Enable drink reminder",
            installRequired: "On iOS/iPadOS 16.4 or later, install Saufdings to your Home Screen and open it there to enable notifications.",
            unsupported: "Push notifications are not supported by this browser.",
            loadError: "Reminder settings could not be loaded. Try again later.",
            unavailable: "Push notifications are not available on this server.",
            enabled: "Drink reminder enabled.",
            disabled: "Drink reminder disabled.",
            denied: "Notifications are blocked. Allow them in your browser settings, then try again.",
            error: "Reminder settings could not be changed. Check your connection and try again.",
        },
        accountData: {
            pendingWarning_one: "There is {{count}} unsynced drink on this device. Requesting account deletion will permanently discard it. Go back to keep it and sync first.",
            pendingWarning_other: "There are {{count}} unsynced drinks on this device. Requesting account deletion will permanently discard them. Go back to keep them and sync first.",
            title: "Your data",
            loading: "Checking your account status…",
            loadError: "Your account status could not be loaded.",
            retry: "Try again",
            drinksExportTitle: "Drinks history",
            drinksExportDescription: "Download your drinks only. Amounts are in millilitres.",
            drinksExportCsv: "Download CSV",
            drinksExportJson: "Download JSON",
            drinksExportLoading: "Preparing your drinks export…",
            drinksExportEmpty: "There are no drinks to export yet.",
            drinksExportSuccess: "Your drinks file is ready.",
            drinksExportError: "Your drinks could not be exported. Try again later.",
            drinksExportTooLarge: "Your history has more than 10,000 drinks and cannot be exported in one file.",
        },
        loading: "Loading your profile",
        authRequired: {
            title: "Login required",
            description: "Sign in to access your profile and track your drinks.",
        },
        hero: {
            greeting: "Hey {{name}}!",
            subtitle: "All your drinks live here. Add a new one or check your stats to see how the night is going.",
        },
        stats: {
            sectionTitle: "Your stats",
            totalVolume: "Total volume logged",
            lastDrink: "Last recorded drink",
            lastDrinkEmpty: "Nothing recorded yet.",
        },
        cta: {
            addDrink: "Add drink",
            viewStats: "View stats",
        },
        eventsSectionTitle: "Events",
        accountSectionTitle: "Your account",
        currentEvent: {
            title: "Current event",
            refresh: "Refresh",
            passwordRequired: "Password required",
            locationUnknown: "No location specified",
            startedLabel: "Started {{time}}",
            startedUnknown: "Unknown",
            endedLabel: "Ended {{time}}",
            leaveButton: "Leave event",
            manageAccess: "Manage access",
            endConfirmTitle: "End this event?",
            endConfirmOk: "End",
            endConfirmCancel: "Cancel",
            endButton: "End event",
            deleteConfirmTitle: "Delete this event?",
            deleteConfirmDescription: "All drinks, participants, and related tournaments will be removed. This cannot be undone.",
            deleteConfirmOk: "Delete",
            deleteButton: "Delete event",
            empty: "You are not currently attending an event.",
        },
        createEvent: {
            title: "Create event",
            form: {
                eventNameLabel: "Event name",
                eventNamePlaceholder: "Birthday party",
                eventNameRequired: "Please enter an event name.",
                locationLabel: "Location",
                locationPlaceholder: "Optional",
                visibilityLabel: "Visibility",
                visibilityRequired: "Select who can see this event.",
                passwordLabel: "Access password",
                passwordPlaceholder: "Enter phrase",
                passwordRequired: "Enter a password for the event.",
                submit: "Start event",
            },
        },
        openEvents: {
            title: "Open events",
            empty: "No open events right now.",
            joinButton: "Join",
            locationFallback: "No location",
            startedLabel: "Started {{time}}",
            leaveCurrentFirst: "Leave your current event first.",
        },
        accessModal: {
            title: "Manage event access",
            visibilityLabel: "Visibility",
            visibilityRequired: "Select a visibility option.",
            passwordLabel: "Access password",
            passwordPlaceholder: "New password",
            passwordRequired: "Enter a password.",
            save: "Save changes",
        },
        passwordModal: {
            title: 'Join "{{event}}"',
            defaultTitle: "Join event",
            okText: "Join",
            cancelText: "Cancel",
            placeholder: "Event password",
        },
        messages: {
            loadProfileError: "Unable to load profile data.",
            loadEventsError: "Unable to load events.",
            createSuccess: "Event created.",
            createError: "Unable to create event.",
            joinSuccess: "Joined event.",
            joinError: "Unable to join event.",
            leaveSuccess: "Left event.",
            leaveError: "Unable to leave event.",
            endSuccess: "Event ended.",
            endError: "Unable to end event.",
            deleteSuccess: "Event deleted.",
            deleteError: "Unable to delete event.",
            updateAccessSuccess: "Event access updated.",
            updateAccessError: "Unable to update event access.",
            passwordPromptEmpty: "Please enter the password.",
            passwordPromptInvalid: "That password was not accepted. Please try again.",
        },
    },
    auth: {
        logoutPendingTitle: "Discard unsynced drinks?",
        logoutPendingDescription_one: "There is {{count}} unsynced drink on this device. Logging out will permanently discard it.",
        logoutPendingDescription_other: "There are {{count}} unsynced drinks on this device. Logging out will permanently discard them.",
        logoutPendingDiscard: "Log out and discard",
        login: "Sign in",
        logout: "Logout",
        password: "Password",
        logoutSuccess: "Logged out. See you soon!",
        logoutError: "Unable to log out. Please try again.",
        logoutPushOffline: "Connect to the internet to remove your reminder subscription before logging out.",
        ssoIdentityUnavailable: "You signed in successfully, but Saufdings cannot identify your account yet. Personal features are unavailable for now.",
        ssoInteractiveFailed: "Sign-in did not complete. Sign in to try again.",
    },
    sidebar: {
        dowtexHub: "Dowtex hub",
        more: "More",
        brandName: "Saufdings",
        mobileHome: "Home",
        navBeerpong: "Beerpong",
        navKingcup: "Kingcup",
        navShitty: "Shitty",
        navProfile: "Profile",
        navAddDrink: "Add Drink",
        navDrinkStats: "Drink Stats",
        navDrinkHistory: "Drink History",
        navEventStats: "Event Stats",
        openMoreNav: "Open more navigation items",
        languageLabel: "Language",
        languageEnglish: "English",
        languageGerman: "German",
        collapse: "Collapse",
        expand: "Expand",
    },
    installPrompt: {
        title: "Install Saufdings",
        description: "Add Saufdings to your home screen for quick access.",
        install: "Install",
        error: "Installation failed. Please try again later.",
        iosInstructions: "Tap Share, then Add to Home Screen to install Saufdings.",
        dismiss: "Dismiss install instructions",
    },
    addDrink: {
        title: "Add a drink",
        subtitle: "Log what you just finished. We will take care of the stats.",
        alert: {
            title: "Drink logged",
            description: "{{amount}} ml of {{name}} added to your stats.",
        },
        offline: {
            iosSyncHint: "Open Saufdings on iPhone or iPad to sync queued drinks.",
            failedDrink: "Could not sync {{drink}}. It is still saved on this device.",
            retry: "Retry",
            remove: "Remove",
            title: "Offline",
            description: "Drinks you log now are saved on your device and sent once you're back online.",
            pending: "{{count}} drink(s) waiting to sync",
            pendingWithFailed: "{{count}} drink(s) waiting to sync, {{failed}} need attention",
            savedDrinkList: "Showing saved drink list",
            noSavedDrinks: "No saved drink list is available offline. Reconnect to load drinks.",
        },
        form: {
            drinkLabel: "Drink",
            drinkPlaceholder: "Select drink",
            sizeLabel: "Drink size",
            customSize: "Custom",
            implausibleEntry: "Check this entry: volume is over 1.5 L or strength is over 96% ABV. For a self-mixed drink, verify the entered volume and strength.",
            whenLabel: "When",
            sizeHelp: "Select a preset to auto-fill the amount. Custom keeps whatever you enter below.",
            finishLabel: "Drink finish",
            startLabel: "Started at",
            startTooltip: "Set when you began sipping. Leave empty to derive from duration.",
            startCurrent: "Currently {{timestamp}}",
            startProvide: "Provide a finish time above to enable this field.",
            durationLabel: "Drinking duration",
            durationTooltip: "Log how long you sipped this drink.",
            durationCustom: "Custom",
            durationHintNoFinish: "Choose a finish time above to derive the start.",
            durationHintChug: "Marked as chugged – added instantly.",
            durationHintStartAt: "Started around {{timestamp}}",
            durationHintFallback: "Started shortly before the finish time.",
            durationPresetUsed: "Using preset {{label}}.",
            durationPresetGeneric: "Using preset duration.",
            durationPresetMinutes: "{{minutes}} min",
        },
        amountPresets: {
            shot: "Shot",
            shotSubtitle: "20 ml",
            double: "Double",
            doubleSubtitle: "40 ml",
            longdrink: "Long drink",
            longdrinkSubtitle: "300 ml",
            beer: "Beer",
            beerSubtitle: "500 ml",
            mass: "Mass",
            massSubtitle: "1 liter",
            custom: "Custom",
            customSubtitle: "Custom amount",
        },
        durationPresets: {
            instant: "Chug",
            quarterHour: "15 min",
            halfHour: "30 min",
            threeQuarterHour: "45 min",
            oneHour: "60 min",
        },
        messages: {
            loginToAdd: "Please login to add drinks.",
            selectDrink: "Select the drink you just had.",
            drinkNotFound: "Selected drink not found. Please try again.",
            invalidAmount: "Amount must be at least 1 ml.",
            loginFirst: "Please login first.",
        },
        notifications: {
            drinkRecorded: "Drink recorded. Cheers!",
            drinkQueued: "Drink saved - will sync when you're back online. Cheers!",
        },
        errors: {
            loadDrinks: "Failed to load drinks.",
            addDrink: "Failed to add drink.",
            removeDrink: "Failed to remove drink.",
        },
        quickAdd: {
            removeOffline: "Reconnect to delete this drink.",
            title: "Recent drinks",
            hint: "Tap once to log a drink",
            loading: "Loading recent drinks",
            empty: "Recent drinks will appear here.",
            uses: "{{count}} logged",
            removeButton: "Remove {{drink}} {{amount}} ml",
            undo: "Undo",
            durationChug: " · chug",
            durationApprox: " · ~{{minutes}} min",
            buttonLabel: "+ {{drink}} ({{amount}} ml, {{count}}{{duration}})",
            unavailable: "{{drinkName}} is no longer available.",
            added: "{{drinkName}} added.",
            addedQueued: "{{drinkName}} saved - will sync when you're back online.",
            removed: "{{drinkName}} removed.",
            alreadySynced: "{{drinkName}} has already synced. Remove it from your recent drinks if needed.",
            noRecent: "No recent {{drinkName}} to remove.",
        },
        when: {
            now: "Now",
            "15": "15 min ago",
            "30": "30 min ago",
            custom: "Custom",
        },
        summary: {
            chooseDrink: "Choose a drink",
            line: "{{drink}}, {{amount}} ml, {{when}} · {{duration}}",
        },
        close: "Close add drink",
        submitLabel: "Add Drink",
        loginRequiredTitle: "Login required",
        loginRequiredDescription: "Sign in to track your drinks and keep the game going.",
        modalTitle: "Add a drink",
    },
    pages: {
        drinkhelper: {
            title: "Drinking Helper",
            subtitleLine1: "You want to drink, but you don't know what game to play?",
            subtitleLine2: "Don't worry, I've got you covered.",
            subtitle: "Pick your group and find your next game.",
            question: "How many players are you?",
            suggestions: "Games for your group",
            empty: "No games match this group yet. Try another player count.",
            loading: "Loading game suggestions",
            gameHint: "A quick party game to get things started.",
            hints: { single: "A solo round", double: "Just the two of you", party: "Bring the whole group" },
            gameDescriptions: {
                vrchat: "Take turns with quick challenges in a social VR world.",
                greekTemple: "Build a temple together through rounds of team challenges.",
                laughfools: "Try to keep a straight face through silly prompts.",
                holzfaellern: "Compete in a playful test of strength and timing.",
                dontDrinkAndDrive: "Answer quick questions and pass the turn around.",
                quizzduell: "Challenge each other with a fast quiz duel.",
                busfahren: "Play through a lively sequence of card guesses.",
                rideTheBus: "Guess your way through a classic party card game.",
                hawaii: "Take turns through a relaxed set of party challenges.",
                rageCage: "Race to bounce a ball into a cup before the next player.",
                lieing: "Bluff with dice and see who can spot the lie.",
                shitty: "Pass the cards and dodge the unlucky one.",
                kingcup: "Draw cards and build toward the final King's Cup.",
                flunky: "Teams take turns throwing to knock down the target.",
                horseRaising: "Race your chosen horse through quick rounds.",
                iti: "A quick-fire party challenge built around bold answers.",
                itiPussy: "A cheekier spin on the quick-fire Iti challenge.",
            },
            players: {
                single: "1 Player",
                double: "2 Players",
                party: "3 Players or more",
            },
        },
        saufAlone: {
            title: "Sauf Alone",
        },
        saufTwo: {
            title: "Sauf Two",
        },
        saufFriends: {
            title: "Sauf With Your Friends",
        },
        music: {
            title: "Enjoy the Music",
            loading: "Loading Spotify Playlist...",
            spotifyTitle: "Spotify Playlist",
            spotifyNotice: "Loading the player connects to Spotify and shares your IP address with them.",
            loadSpotify: "Load Spotify player",
        },
    },
    games: {
        names: {
            higherOrLower: "Higher or Lower",
            vrchat: "VRChat",
            greekTemple: "Greek temple",
            beerpong: "Beerpong",
            laughfools: "Laughfools",
            holzfaellern: "Holzfällern",
            dontDrinkAndDrive: "Don't drink and drive",
            quizzduell: "Quizzduell",
            busfahren: "Busfahren",
            rideTheBus: "Ride the bus",
            hawaii: "Hawaii",
            rageCage: "Rage Cage",
            lieing: "Lieing",
            shitty: "Shitty",
            kingcup: "Kingcup",
            flunky: "Flunky Ball",
            horseRaising: "Horse Raising",
            iti: "Iti",
            itiPussy: "Iti pussy version",
        },
        details: {
            higherOrLower: {
                title: "Higher or lower",
                tagline: "The Simple Card Game",
                description: "Try your luck: Predict the next card! Fast to play, simple to learn—a crowd favorite for any party.",
                requirementsTitle: "Requirements",
                requirements: ["Deck of cards", "Drinks for everyone"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Each player gets a card from the deck.",
                    "On your turn, predict if the next card will be higher or lower than your last card.",
                    "Guess correctly? Give a shot to another player. Guess wrong? Drink a shot yourself!",
                ],
                summary: "Keep guessing while the cards are in your favor—and drink when they are not.",
            },
            beerpong: {
                description: "A quick head-to-head cup game: aim for the opposing team's cups.",
                tournamentTitle: "Organize Your Beer Pong Tournament",
                tournamentDescription:
                    "Ready to take your beer pong night to the next level? Use the dedicated tournament manager to create brackets, keep track of results, and guide teams through winner and loser brackets.",
                tournamentNote: "Ps: Only the tournament creator can adjust matchups, advance winners, or close out the bracket.",
                tournamentButton: "Open tournament manager",
                rules: {
                    heading: "Beer Pong Rules",
                    intro: "It helps to align on the rule set before the first throw.",
                    visibleSets: "Visible rule sets:",
                    empty: "No rules in this set.",
                    noMatches: "No rules match the current filters.",
                    offlineTitle: "Offline - showing saved rules",
                    offlineDescription: "These are the rules from your last successful sync, not live.",
                    savedTag: "Saved {{when}}",
                },
                rulesError: "Unable to load beer pong rules.",
                event: {
                    loading: "Loading tournament data...",
                },
            },
            busfahren: {
                title: "Ride the Bus Rules",
                tagline: "Classic Drinking Game",
                requirementsTitle: "Requirements",
                requirements: ["A full deck without jokers", "Drinks for all players"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Take turns drawing cards and guessing red or black, higher or lower, in between or outside, and the suit.",
                    "Lay out the revealed cards as a 5-4-3-2-1 pyramid. Matching cards let you hand out drinks based on the row.",
                    "The player with the most cards rides the bus by guessing higher or lower for five cards in a row.",
                    "Each wrong guess is a drink; a flawless run lets the bus driver pass the job on.",
                ],
                summary: "Agree on your regional rules before playing.",
                description:
                    "Ride the Bus is played with a full deck of cards. Whoever makes the most mistakes during the rounds has to “ride the bus”—and drink!",
                variant: "🌍 Variant: Rules may vary by region. Agree on your set before starting!",
                steps: {
                    setup: {
                        title: "Setup",
                        summary: "Shuffle the deck, all players sit in a circle.",
                        detail: [
                            "You’ll need a full deck (without jokers). Everyone sits around a table or in a circle, ready for action.",
                        ],
                        list: [],
                        tip: "",
                    },
                    roundOne: {
                        title: "Round 1: The Card Stack",
                        summary: "Everyone takes turns drawing cards and guessing.",
                        detail: ["Take turns drawing a card and make these predictions:"],
                        list: [
                            "Red or Black?",
                            "Higher or Lower than the last card?",
                            "In between or Outside?",
                            "Suit: Hearts, Diamonds, Spades, or Clubs?",
                        ],
                        tip: "Tip: For every mistake, take a drink!",
                    },
                    roundTwo: {
                        title: "Round 2: The Pyramid",
                        summary: "Build the pyramid, give out drinks for matches.",
                        detail: [
                            "Lay out the revealed cards in a pyramid (5-4-3-2-1). If you have a matching card, hand out drinks:",
                        ],
                        list: ["Row of 5: 1 drink", "Row of 4: 2 drinks", "Row of 3: 3 drinks", "...and so on."],
                        tip: "",
                    },
                    finale: {
                        title: "Final: The Bus Driver",
                        summary: "Most cards? You ride the bus!",
                        detail: [
                            "The player with the most cards becomes the bus driver. Reveal 5 cards in a row and guess higher or lower each time.",
                        ],
                        list: [],
                        tip: "Each mistake is a drink! Make it through flawlessly and you can pass the job.",
                    },
                },
            },
            dontDrinkAndDrive: {
                title: "Don't Drink and Drive",
                tagline: "Party Racing Challenge",
                instructionsTitle: "How to Play",
                instructions: [
                    "Gather everyone and set up the racing game.",
                    "Race while finishing your drink. Pull over before drinking; never drink and drive at the same time.",
                    "The first to finish both wins. The last player gets a group-chosen penalty.",
                ],
                summary: "Keep drinking and driving separate, even on the virtual track.",
                description:
                    "Combine racing games and drinks for a hilarious challenge where skill, speed, and strategy all matter. Just remember: safety first—even on the virtual track!",
                requirementsTitle: "Requirements",
                requirements: ["Drinks", "A console with a racing game (ideally Mario Kart)"],
                ruleInstruction: "Every time you want to drink, pull over to the side of the track.",
                steps: [
                    {
                        title: "Get Ready",
                        description: "Gather all players and set up your racing game.",
                    },
                    {
                        title: "The Challenge",
                        description:
                            "Everyone joins a race. Finish both your drink and the race as fast as possible.",
                        ruleTitle: "Key Rule:",
                        ruleHint: "No drinking and driving at the same time!",
                    },
                    {
                        title: "The Winner & The Punishment",
                        description:
                            "The first to finish the race and the drink wins. The last player gets a fun penalty—decide as a group.",
                    },
                ],
                warning: "🚦 Remember: It's all for fun—never drink and drive in real life!",
            },
            flunky: {
                title: "Flunky Ball",
                tagline: "Outdoor Fun",
                description: "A competitive and energetic outdoor game, great for larger groups. Perfect for parties or summer gatherings.",
                requirementsTitle: "Requirements",
                requirements: ["One ball per team", "One bottle of beer per player", "A spacious outdoor area"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Divide players into two teams, each with a bottle.",
                    "Place a bottle in the center and try to knock over the opponents' bottle. If it falls, that player takes a penalty drink.",
                    "As soon as a team hits the center bottle, they can start drinking. Cheers!",
                    "Once the bottle is back up and everyone is behind their own bottle, the other team must stop drinking. First team to finish wins.",
                ],
                summary:
                    "Flunky Ball is action-packed and demands coordination. Teams knock over bottles, race to grab them, and chug when theirs falls—perfect for big groups!",
            },
            hawaii: {
                title: "Hawaii",
                tagline: "Creative Flick Game",
                description: "Flick a bottle cap across a hand-drawn board of player islands and special rules.",
                requirementsTitle: "Requirements",
                requirements: ["A flat surface and something to draw on", "A pen", "A bottle", "Bottle caps"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Draw two touching circles around a bottle to mark Hawaii, then give each player an island circle with their name.",
                    "Take turns flicking a bottle cap onto the board.",
                    "If the cap lands on an island, its owner drinks. If it lands on Hawaii, everyone drinks.",
                    "If the cap misses, draw a smaller island and make up a rule for it.",
                    "Pass the turn and keep playing until the board is full or the group stops.",
                    "Optional: Connect two islands so both rules apply.",
                ],
                summary: "Invent island rules together and keep each turn moving.",
                setup: {
                    title: "Setup",
                    line1: "Grab something to draw on (a pizza box works great) and something to write with.",
                    line2: "Place a bottle in the center and draw two touching circles—this is your Hawaii!",
                    line3: 'Each player draws their own “island” circle and writes their name inside.',
                },
                rulesTitle: "How to Play",
                rules: {
                    flick: "The first player flicks a beer cap onto the board.",
                    hitIsland: {
                        question: "Hit someone’s island?",
                        answer: "That player drinks!",
                    },
                    hitHawaii: "Hit Hawaii?",
                    everyoneDrinks: "Everyone drinks!",
                    miss: {
                        question: "Miss everything?",
                        answer:
                            "Draw a new smaller circle somewhere on the board and add a special rule/game for it. Whenever it’s hit, that rule is played.",
                    },
                    rotate: "Turns move clockwise. Continue until the board is full or you’re satisfied.",
                    twist: {
                        title: "Optional Twist:",
                        description: "Combine two circles into one bigger one—now both rules trigger at once!",
                    },
                },
                exampleTitle: "Example",
                exampleAlt: "Hawaii setup",
                tip: {
                    title: "Tip:",
                    text: "The stranger the island rules, the better!",
                },
                footer:
                    "Get creative and unleash wild ideas—the islands can be as silly (or brutal) as you want. You can also add islands for extra games.",
            },
            kingCup: {
                title: "King’s Cup Rules",
                tagline: "Let’s fckng do it – whoop whoop!",
                description:
                    "Draw cards from the circle. Break the ring and you chug—either the King’s Cup or your own drink, depending on your house rules.",
                requirementsTitle: "Requirements",
                requirements: ["A deck of playing cards", "A central cup and drinks"],
                instructionsTitle: "How to Play",
                instructions: ["Draw a card from the circle and follow the rule shown for that card."],
                summary: "🍻 Tip: Think of a rule before you draw! “Schurik always drinks” is fair game, but it might backfire.",
                hint: "🍻 Tip: Think of a rule before you draw! “Schurik always drinks” is fair game, but it might backfire.",
                cards: {
                    king: {
                        label: "King",
                        rule: "The Ruler",
                        description: "Make a rule and drink as many sips as kings drawn. Last king drains the cup.",
                    },
                    queen: {
                        label: "Queen",
                        rule: "Question Master",
                        description: "Ask questions—whoever answers drinks. (Game-related answers don’t count.)",
                    },
                    jack: {
                        label: "Jack",
                        rule: "Never Have I Ever",
                        description: "Say something you’ve never done. Everyone who has must drink.",
                    },
                    ten: {
                        label: "10",
                        rule: "Ten is Men",
                        description: "Bottoms up, boys.",
                    },
                    nine: {
                        label: "9",
                        rule: "Nine is Rhyme",
                        description: "Start a rhyme—keep it going. First to fail or repeat drinks.",
                    },
                    eight: {
                        label: "8",
                        rule: "Eight is Mate",
                        description: "Choose a mate who drinks whenever you drink (until the next 8).",
                    },
                    seven: {
                        label: "7",
                        rule: "Seven to Heaven",
                        description: "Point skyward whenever you like. Last player to follow drinks. (Resets with next 7.)",
                    },
                    six: {
                        label: "6",
                        rule: "Six is Chicks",
                        description: "Ladies drink.",
                    },
                    five: {
                        label: "5",
                        rule: "Categories",
                        description: "Pick a category (e.g., car brands). First to repeat or fail drinks.",
                    },
                    four: {
                        label: "4",
                        rule: "Four You",
                        description: "Pick someone else to drink.",
                    },
                    three: {
                        label: "3",
                        rule: "Three for Me",
                        description: "You drink!",
                    },
                    two: {
                        label: "2",
                        rule: "Double Down",
                        description: "Your drinks count double until the next 2 is drawn.",
                    },
                    ace: {
                        label: "Ace",
                        rule: "Waterfall",
                        description: "Everyone drinks as long as the player who drew the ace drinks.",
                    },
                },
            },
            holzfaellern: {
                title: "Holzfällern",
                tagline: "Bottle Cap Fun",
                description: "A competitive party classic—easy to set up with fierce, friendly rivalry. Best played 1 vs 1, but works with bigger groups too.",
                requirementsTitle: "Requirements",
                requirements: ["One bottle per player", "At least one more bottle cap than players"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Sit facing your opponent(s)—usually on the floor—about two foot lengths apart.",
                    "Place your bottle between your legs so it stands upright and won’t topple.",
                    "Balance a bottle cap (upside down) on top of your bottle.",
                    "Take turns tossing remaining caps at the other bottles to knock their caps off.",
                ],
                summary:
                    "Knock the cap off your opponent’s bottle and they drink! Agree beforehand how many hits or rounds are needed before someone finishes their bottle.",
            },
            horseRaising: {
                title: "Horse Raising (Card Version)",
                tagline: "Card & Drinking Game",
                description:
                    "A card-based drinking game where aces represent horses. Bet on your favorite and hope it crosses the finish line first!",
                requirementsTitle: "Requirements",
                requirements: [
                    "One deck of cards (with aces as horses)",
                    "A flat surface for the racetrack",
                    "Drinks for all players",
                ],
                instructionsTitle: "How to Play",
                instructions: [
                    "Shuffle the deck and lay four face-down cards in a row—this is the track.",
                    "Each suit (hearts, diamonds, spades, clubs) represents a horse; its ace marks the start.",
                    "Players bet drinks on a horse. Win and you can hand out the doubled drinks; lose and your bet is gone.",
                    "Flip cards from the remaining deck. When all horses pass one of the track cards, flip it over and move the matching suit back.",
                    "First horse to clear all track cards wins. The player backing that horse makes others drink.",
                ],
                summary:
                    "Fast, luck-driven, and perfect for competitive groups—cheer on your horse and hope the deck is on your side.",
            },
            greekTemple: {
                title: "Greek temple",
                tagline: "Card Game of Gods & Guesswork",
                description: "Build three card pillars by predicting whether each next card is higher or lower.",
                requirementsTitle: "Requirements",
                requirements: ["A deck of cards", "Drinks for the fallen pillars"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Shuffle the deck and lay three cards face up as the pillars.",
                    "On your turn, add one card to an end of each pillar, guessing higher or lower first.",
                    "A correct guess keeps the card; a wrong guess collapses that pillar and returns its cards to the deck.",
                    "Reset the pillars if everyone fails or cards run out. Each collapsed pillar costs one shot.",
                ],
                summary: "Keep all three pillars standing for as long as you can.",
                steps: {
                    setup: {
                        title: "Setup",
                        description: "Prepare the table and the deck.",
                    },
                    turns: {
                        title: "Player Turns",
                        description: "How to play your round.",
                    },
                    next: {
                        title: "Next Players",
                        description: "What happens after a success or failure.",
                    },
                    reset: {
                        title: "Reset & Endgame",
                        description: "How the temple resets or ends.",
                    },
                },
                setup: {
                    heading: "Setup:",
                    line1: "Shuffle a full deck of cards and place three face-up cards in a row—the pillars.",
                    line2: "Each pillar is a temple column. Players add cards to either end.",
                    footer: "These are your temple pillars — the foundation of the game.",
                },
                turns: {
                    line1: "Each turn, add one card to each pillar. Choose top or bottom.",
                    line2: "Before placing, guess whether your card will be",
                    higher: "higher",
                    lower: "lower",
                    or: "or",
                    pillar: "Pillar {{id}}",
                    prompt: "Guess: Higher or Lower?",
                    correct: "Correct guesses stay. Move to the next pillar.",
                    wrong: "Wrong guess collapses the pillar. Cards go back to the deck.",
                },
                next: {
                    line1: "After you succeed, the next player faces taller pillars.",
                    line2: "They again choose an end and guess higher/lower.",
                    collapse: "If a player fails, the pillar collapses and restarts with one card.",
                    goal: "🏛 Goal: finish all pillars successfully!",
                    drink: "🍻 Drinking rule: each failed pillar = 1 shot. Collapse all three? Drink 3 shots and restart.",
                },
                reset: {
                    line1: "If everyone fails or the deck is low, reshuffle and reset.",
                    line2: "Play as long as you want—count successful rounds or just vibe.",
                    footer: "🥂 The temple may crumble, but the spirits stay strong!",
                },
            },
            shitty: {
                title: "Shitty Game",
                tagline: "Game to get shit-faced",
                description: "A card game of quick guesses, escalating decisions, and a shared endgame.",
                requirementsTitle: "Requirements",
                requirements: ["A deck of playing cards", "At least two players"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Predict red or black, under or above, between or outside, then the card suit.",
                    "When everyone has four cards, matching a card in the crown ring ends the turn for everyone.",
                    "Lay out nine cards for the endgame. Matching values use the Good or Evil row multiplier, and everyone drinks the value of the last card.",
                ],
                summary: "Make the card predictions, then finish with the Good and Evil endgame rows.",
                steps: {
                    decision: {
                        title: "Decision Rounds",
                        description: "Make your choices: Red or black? Under or above?",
                    },
                    endgame: {
                        title: "Endgame",
                        description: "Final layout and last rules.",
                    },
                },
                decision: {
                    heading: "Decision Round",
                    cards: {
                        redBlack: {
                            title: "Red or Black?",
                            win: "Give one shot to another player.",
                            lose: "Drink one shot yourself.",
                        },
                        underAbove: {
                            title: "Under or Above? (Or Same)",
                            win: "Give two shots to another player.",
                            lose: "Drink two shots yourself.",
                        },
                        betweenOutside: {
                            title: "Between or Outside? (Or Same)",
                            win: "Give one shot to another player.",
                            lose: "Drink one shot yourself.",
                        },
                        sameSuit: {
                            title: "Same Suit?",
                            win: "Give three shots to another player.",
                            lose: "Drink three shots yourself.",
                        },
                    },
                    progressive: {
                        title: "The Progressive Decision",
                        description1:
                            "Everyone now has four cards. If your card matches one in the crown ring, throw it into the middle.",
                        description2: "Doing so ends the turn for everyone!",
                        win: "Matching card? Hand out five drinks.",
                        lose: "No match? Drink five yourself.",
                        hint: "🍻 Hint: Go for a rainbow—if you hold every suit, it’s basically a double down.",
                    },
                },
                endgame: {
                    heading: "Endgame",
                    layoutLine1: "Lay out nine cards as shown.",
                    layoutLine2: "Call the top row “Good” and the bottom row “Evil.” Matching suits win or lose drinks with multipliers.",
                    backgroundAlt: "Background card",
                    paragraphs: [
                        "Flip cards; if you have the same value, place it on the layout.",
                        "Good row: give out drinks using the card multiplier.",
                        "Evil row: drink yourself with the multiplier.",
                        "The last card is flipped and everyone drinks its face value.",
                    ],
                },
            },
            iti: {
                title: "Iti (Card Slap Game)",
                tagline: "Reaction & Drinking Game",
                description: "Fast-paced slap game. Be the first to clear your cards by smacking the table when a match appears—just don’t slap incorrectly.",
                requirementsTitle: "Requirements",
                requirements: ["One deck of cards", "At least 2 players", "A table to slap"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Split the deck evenly. Keep piles face-down.",
                    "Flip your top card into a shared pile.",
                    "If the flipped card matches another on the table, players with that card slap the table.",
                    "Slowest player (or wrong slap) takes all open cards—and drinks.",
                ],
                summary:
                    "Quick reflexes and sharp eyes win the day. Clear your cards first, slap accurately, and avoid drinking penalties.",
            },
            itiSequential: {
                title: "Iti (Sequential Card Slap Game)",
                tagline: "Reaction & Drinking Game",
                description:
                    "Variation where everyone places cards in order. Call the card correctly and slap fast; last to slap takes the pile.",
                requirementsTitle: "Requirements",
                requirements: ["One deck of cards", "At least 2 players", "A table to slap"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Stack all cards in the center face-down.",
                    "Players place cards in order: 2,3,...,Ace, then loop.",
                    "When someone calls and plays the correct card, everyone slaps.",
                    "Last to slap—or anyone who misplays—takes the pile and drinks.",
                ],
                summary:
                    "Keep the rhythm, watch the calling order, and slap lightning-fast to avoid extra cards (and extra drinks).",
            },
            quizzler: {
                title: "Quizzler",
                tagline: "What Dafuq Is Going On",
                description: "Take turns answering general-knowledge questions in a fast quiz duel.",
                requirementsTitle: "Requirements",
                requirements: ["A quizmaster", "Trivia questions"],
                instructionsTitle: "How to Play",
                instructions: [
                    "Pick a quizmaster and form teams or play solo.",
                    "The quizmaster asks general-knowledge questions.",
                    "Wrong answer? Drink!",
                    "Right answer? Others drink, or the winner creates a new rule.",
                ],
                summary: "Use internet trivia, quiz cards, or make up your own. The crazier the better.",
                steps: {
                    pick: "Pick a quizmaster and form teams or play solo.",
                    ask: "Quizmaster asks any general-knowledge questions.",
                    wrong: "Wrong answer? Drink!",
                    right: "Right answer? Others drink—or winner creates a new rule.",
                },
                extra: "Use internet trivia, quiz cards, or make up your own. The crazier the better.",
                tip: "Tip: Personal or themed questions keep it spicy. Quizmaster’s decision is final!",
            },
            laughfools: {
                title: "Laughfool",
                tagline: "The No-Laugh Challenge",
                description: "Try to keep a straight face! If you laugh, you lose—and you drink. Perfect for a group with a sense of humor.",
                requirementsTitle: "Requirements",
                requirements: [
                    "A device to look up jokes (phone, tablet, etc.)",
                    "A group of friends ready to laugh",
                    "Drinks of your choice",
                ],
                howToTitle: "How to Play",
                steps: [
                    "All players take turns telling jokes to each other.",
                    "Every time a player laughs, they must take a drink.",
                    "Want to spice it up? Watch a funny video or movie together—if you laugh, you drink!",
                ],
                instructionsTitle: "How to Play",
                instructions: [
                    "All players take turns telling jokes to each other.",
                    "Every time a player laughs, they must take a drink.",
                    "Want to spice it up? Watch a funny video or movie together—if you laugh, you drink!",
                ],
                summary: "😁 Will you keep it together, or be the first Laughfool?",
                restrictionsTitle: "Optional Restrictions/Variants",
                restrictions: [
                    "Limit joke length (short only or minimum length).",
                    "Pick joke categories such as family friendly, dark, dirty, or themed.",
                ],
                footer: "😁 Will you keep it together, or be the first Laughfool?",
            },
            rageCage: {
                title: "Rage Cage",
                tagline: "Gas-Gas-GasGasGasGas",
                description: "Fast-paced, loud and competitive. Try to keep up or drink up! 🍻",
                requirementsTitle: "Requirements",
                requirements: [
                    "Some cups (at least 10)",
                    "At least two ping pong balls",
                    "A table",
                ],
                instructionsTitle: "How to Play",
                instructions: [
                    "Two players facing each other grab a cup, toast, and drink.",
                    "Bounce your ball into your empty cup.",
                    "If you score, pass the cup and ball to your right; a first-try bounce lets you pass to anyone.",
                    "If cups line up in front of a player, stack them. They grab a fresh cup from the middle and catch up.",
                    "If you hit a cup in the middle, drink it.",
                ],
                summary: "Stuck? Keep the pace fast for max fun. Slowpokes drink!",
                setupTitle: "Setup",
                setupDescription: "Place all cups in a circle or cluster in the center of the table.\nAlmost any arrangement works!",
                setupImages: [
                    { alt: "Rage Cage basic setup", caption: "A simple starter setup 👆" },
                    { alt: "Rage Cage wild setup", caption: "Go wild: Add shots in some cups for extra chaos 🥃" },
                ],
                setupTip: "Pro tip: Mix some special drinks or vary the amount of liquid for more fun!",
                howToTitle: "How to Play",
                steps: [
                    { title: "Start", description: "Two players facing each other each grab a cup, toast, and drink." },
                    { title: "Bounce!", description: "Try to bounce your ball into your empty cup." },
                    {
                        title: "Pass it on",
                        description: "If you score, pass the cup and ball to the player on your right.",
                        note: "Sink your bounce on the first try? Pass your cup to anyone at the table.",
                    },
                    {
                        title: "Overlap",
                        description: "If cups line up in front of a player, stack them! The unlucky one must grab a fresh cup from the middle and catch up.",
                    },
                    { title: "Hit a cup in the middle?", description: "Drink it!" },
                ],
                paceTip: "Stuck? Keep the pace fast for max fun. Slowpokes drink!",
                footer: "I swear to god, if you forget to put on gas, I will find you...",
            },
            lieingOverview: {
                title: "Lieing variants",
                subtitle: "You feel like lieing? Choose your poison!",
                tagline: "Choose your bluff",
                description: "Choose the cards or dice variant to see its rules.",
                requirementsTitle: "Choose a version",
                requirements: ["Cards or dice"],
                instructionsTitle: "Choose a variant",
                instructions: ["Open Cards or Cubes to see how to play."],
                summary: "Pick the version your group wants to play.",
                variants: {
                    cards: {
                        title: "Cards",
                        description: "Every player gets cards and lies about them.",
                    },
                    cubes: {
                        title: "Cubes",
                        description: "Let's challenge our luck.",
                    },
                },
            },
            lieingCards: {
                title: "Lying Cards",
                tagline: "Bluff Card Game",
                description: "Lose your cards by playing in order—or lying about it! Catch the liars and make them drink!",
                requirementsTitle: "Requirements",
                requirements: ["Deck of cards", "Drinks for all players"],
                rulesTitle: "How to Play",
                instructionsTitle: "How to Play",
                instructions: [
                    "Hand out the cards evenly between all players.",
                    "Place cards face down and state which card it should be in ascending order. You do not have to tell the truth.",
                    "Call cap to challenge the last claim. If it was a lie, the liar takes all played cards. If it was true, the challenger takes them.",
                    "Each time you take the stack, you drink.",
                ],
                summary: "Beware, this may cause intoxication and ruin friendships...",
                rules: {
                    deal: "Hand out the cards evenly between all players.",
                    declare: "Place cards on the pile <strong>face down</strong>, while stating which card it should be (ascending order). <italic>You don’t have to tell the truth!</italic>",
                    challenge: "Anyone can call <code>cap</code> to challenge the last claim. If it was a lie, the liar takes all played cards. If it was true, the challenger must take them!",
                    drink: "Each time you take the stack, you drink!",
                },
                exampleTitle: "Example",
                honestExample: "When playing cards honestly, it should look like:",
                lieExample: "But you can also lie. It might look like:",
                tooltip: "If a player calls <code>cap</code>, they check if the claimed card matches. Liar takes all cards if caught. Otherwise the challenger does!",
                tooltipTrigger: "Call <code>cap</code> at any time to check if someone is lying.",
                footer: "Beware, this may cause intoxication and ruin friendships...",
            },
            lieingCubes: {
                title: "Lieing Cubes",
                tagline: "Bluff Dice (with Two Cubes)",
                description: "Bluff your way to glory with two cubes. Who dares, who risks, who drinks?",
                requirementsTitle: "Requirements",
                requirements: [
                    "Two dice",
                    "One cup",
                    "Something to cover the cup (like cardboard)",
                ],
                howToTitle: "How to Play",
                instructionsTitle: "How to Play",
                instructions: [
                    "Roll two dice in secret inside the cup, covered with cardboard.",
                    "Peek at your roll and announce any result, truth or lie.",
                    "The next player must roll a higher combo or call your bluff.",
                    "If you lied, you drink. If you told the truth, the challenger drinks.",
                ],
                summary: "For the brave: After each round, pass the cup and keep the bluffing strong! Maybe don't even look.",
                steps: [
                    {
                        title: "Roll in Secret",
                        description: "First player rolls <strong>two dice</strong> inside the cup, covered with cardboard.",
                    },
                    {
                        title: "Peek and Bluff",
                        description: "Peek at your roll. Announce any result aloud—<strong>truth or lie</strong>!",
                    },
                    {
                        title: "Challenge!",
                        description: "Next player must roll a <strong>higher</strong> combo (see below), or call your bluff.",
                    },
                    {
                        title: "Reveal & Drink",
                        description: "<underline>If you lied:</underline> <danger>You drink.</danger>\n<underline>If you told the truth:</underline> <success>Challenger drinks!</success>",
                    },
                ],
                rankingsTitle: "Rankings",
                rankingsIntroTitle: "What is higher than what?",
                rankingsIntroDescription: "Higher combo always beats lower!",
                rankingsNote: [
                    "<strong>21</strong> is the <warning>highest combination</warning>—nothing beats it!",
                    "If a player claims 21, you can only challenge or attempt to roll it yourself.",
                ],
                footer: "For the brave: After each round, pass the cup and keep the bluffing strong! Maybe don't even look.",
            },
        },
    },
    tournamentUi: {
        createTitle: "Create tournament",
        matchDetails: "Match details",
        event: "Event {{id}}",
        chooseEvent: "Choose an active event first.",
        pass: "Pass",
        passHint: "advances automatically",
        bracketScrollHint: "Scroll horizontally to see each round.",
        eliminatedEntrants: "Eliminated entrants",
        eliminated: "Eliminated",
        eliminatedRound: "Eliminated in round {{round}}",
        status: { draft: "Draft", running: "Running", completed: "Completed", cancelled: "Cancelled" },
        entrants: { active: "Active", out: "Withdrawn", withdraw: "Withdraw", reinstate: "Reinstate", summary: "{{active}} active, {{out}} withdrawn", empty: "No entrants yet." },
    },
    beerpong: {
        tournament: {
            entrants: { active: "Playing", out: "Eliminated", eliminate: "Eliminate", restore: "Restore", summary: "{{active}} playing, {{out}} eliminated", empty: "No teams yet." },
            backButton: "Beer Pong overview",
            title: "Tournament Manager",
            sections: {
                setupTitle: "Tournament details",
                setupDescription:
                    "Define your bracket parameters and enter the teams taking part. You can adjust styles later while the tournament is active.",
                listTitle: "Tournaments",
                detailsDrawerTitle: "Tournament details",
            },
            signInNotice: "Sign in to save tournaments and unlock bracket management.",
            searchPlaceholder: "Search tournaments by name",
            form: {
                title: "Create a tournament",
                nameLabel: "Tournament name",
                styleLabel: "Tournament format",
                visibilityLabel: "Visibility",
                passwordLabel: "Tournament password",
                teamTitle: "Team {{number}}",
                removeTeam: "Remove",
                teamNameLabel: "Team name",
                participantLabel: "Participant {{number}}",
                removeParticipant: "Remove",
                addParticipant: "Add participant",
                addTeam: "Add team",
                submit: "Start tournament",
                hintMinTeams: "At least two teams are required to start a tournament.",
            },
            list: {
                winnerLabel: "Winner:",
                categories: {
                    active: "Active tournaments",
                    completed: "Completed tournaments",
                    archived: "Archived tournaments",
                },
                empty: {
                    default: "No tournaments.",
                    active: "No active tournaments.",
                    completed: "No completed tournaments yet.",
                    archived: "No archived tournaments.",
                },
                refresh: "Refresh",
            },
            styles: {
                roundRobin: "Round Robin",
                singleElimination: "Single Elimination",
                doubleElimination: "Double Elimination",
                hybrid: "Hybrid",
            },
            status: {
                active: "Active",
                completed: "Completed",
                cancelled: "Cancelled",
            },
            visibility: {
                public: "Public",
                friends: "Friends",
                password: "Password protected",
                private: "Private",
            },
            messages: {
                signInRequired: "Please sign in to start a tournament.",
                nameRequired: "Please enter a tournament name.",
                minTeams: "You need at least two teams.",
                teamNeedsParticipant: "Team \"{{team}}\" needs at least one participant.",
                passwordRequired: "Please set a password for the tournament.",
                loadTournamentsError: "Unable to load tournaments.",
                createSuccess: "Tournament created!",
                byeInfo: "One team received a bye for the first round. You can adjust matchups at any time.",
                createError: "Unexpected error while creating the tournament.",
                signInToUpdate: "You must be signed in to perform this action.",
                matchUpdated: "Match updated.",
                matchPairingUpdated: "Match pairing updated.",
                updateMatchError: "Unable to update match.",
                tournamentUpdated: "Tournament updated.",
                updateStatusError: "Unable to update tournament status.",
                styleUpdated: "Tournament style updated.",
                styleUpdateError: "Unable to update tournament style.",
                accessUpdated: "Tournament access updated.",
                accessUpdateError: "Unable to update tournament access.",
                enterPassword: "Enter the tournament password.",
                unlockSuccess: "Tournament unlocked.",
                unlockError: "Unable to unlock tournament.",
                teamEliminated: "Team \"{{team}}\" eliminated.",
                teamEliminateError: "Unable to eliminate team.",
                advanceSuccess: "Next round generated.",
                advanceError: "Unable to generate the next round.",
                teamRestored: "Team \"{{team}}\" restored.",
                teamRestoreError: "Unable to restore team.",
                deleteSuccess: "Tournament deleted.",
                deleteError: "Unable to delete tournament.",
            },
            details: {
                empty: "Select a tournament to see the full bracket.",
                noParticipants: "No participants added.",
                match: {
                    pending: "Pending",
                    teamSlot: "Team {{slot}}",
                    tbd: "TBD",
                    selectTeam: "Select team",
                    winnerTag: "Winner",
                    eliminatedTag: "Eliminated",
                    eliminatedRound: "Eliminated · Round {{round}}",
                    label: "Match #{{number}}",
                    winnerLabel: "Winner",
                    cancelledTag: "Cancelled",
                    bye: "Pass: {{team}} advances automatically.",
                    pass: "Pass",
                    passHint: "advances automatically",
                },
                conflict: {
                    alsoPlays: "{{team}} also plays {{match}} in this round.",
                    swap: "Swap",
                    putsThere: "puts {{team}} there instead",
                    keepBoth: "Keep both",
                    cancel: "Cancel",
                    emptySlot: "an empty slot",
                },
                status: { active: "Running", completed: "Completed", cancelled: "Cancelled" },
                matches: {
                    empty: "No matches scheduled yet.",
                    roundTitle: "Round {{round}}",
                    compactTitleWithRound: "Match #{{number}} · Round {{round}}",
                    compactTitle: "Match #{{number}}",
                },
                brackets: {
                    winners: "Winner bracket",
                    losers: "Loser bracket",
                    finals: "Finals",
                },
                tags: {
                    passwordRequired: "Password required",
                    managedByYou: "Managed by you",
                    viewOnly: "View only",
                },
                meta: {
                    createdAt: "Created {{date}}",
                    creatorOnly: "Only the tournament creator can make changes.",
                },
                actions: {
                    manageAccess: "Manage access",
                    generateNextRound: "Generate next round",
                    complete: "Complete",
                    cancel: "Cancel",
                    delete: "Delete",
                    unlock: "Unlock tournament",
                    restoreTeam: "Restore team",
                    eliminateTeam: "Eliminate team",
                },
                popconfirm: {
                    finishTitle: "Finish this tournament?",
                    finishDescription: "Mark this tournament as completed.",
                    cancelTitle: "Cancel this tournament?",
                    cancelDescription: "Canceling will keep the history but stop future matches.",
                    deleteTitle: "Delete this tournament?",
                    deleteDescription: "All teams and matches will be removed permanently.",
                    eliminateTitle: "Eliminate this team?",
                },
                alerts: {
                    passwordTitle: "Password required",
                    passwordDescription: "Enter the tournament password to view the bracket and match details.",
                },
                forms: {
                    unlockPasswordLabel: "Tournament password",
                    unlockPasswordRule: "Enter the tournament password.",
                    unlockPasswordPlaceholder: "Tournament password",
                    accessModalTitle: "Manage tournament access",
                    accessModalSave: "Save changes",
                    visibilityLabel: "Visibility",
                    visibilityRule: "Select a visibility option.",
                    passwordLabel: "Password",
                    passwordRule: "Enter a password.",
                    passwordPlaceholder: "New password",
                    passwordRemovalNotice: "Switching away from password protection will remove the existing password.",
                },
                roundRobin: {
                    leaders_one: "Round robin leader",
                    leaders_other: "Round robin leaders",
                    winners_one: "Round robin winner",
                    winners_other: "Round robin co-winners",
                },
                sections: {
                    teams: "Teams",
                    matches: "Matches",
                },
            },
        },
    },
    drinkStats: {
        authRequired: {
            title: "Login required",
            description: "Sign in to see your drinking streaks and stats.",
        },
        page: {
            title: "Drinking overview",
            subtitle:
                "Overview for {{date}} (keeps late nights together by extending the window around your session).",
        },
        summary: {
            volumeDay: "Selected day ({{date}})",
            volumeWeek: "7 days up to {{date}}",
            volumeMonth: "30 days up to {{date}}",
            alcoholDay: "Pure alcohol on selected day",
            alcoholWeek: "Pure alcohol across 7 days",
            alcoholMonth: "Pure alcohol across 30 days",
            promilleWeek: "Peak estimated promille across 7 days",
            promilleMonth: "Peak estimated promille across 30 days",
        },
        recentDrinks: {
            deleteOffline: "Reconnect to delete a drink.",
            title: "Recent drinks",
            refresh: "Refresh",
            edit: "Edit",
            delete: "Delete",
            deleteConfirm: "Delete this entry?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            noTimestamp: "No timestamp",
            entryTitle: "{{drink}} - {{amount}} ml",
            durationChugged: "Chugged.",
            durationSimple: "≈{{minutes}} min",
            durationWithStart: "≈{{minutes}} min (started {{time}})",
            empty: "No drinks tracked in the {{date}} window.",
            abvLabel: "{{abv}}% ABV",
        },
        metabolism: {
            weightLabel: "Your weight",
            weightHelp: "Used to estimate promille in the chart below.",
            sexLabel: "Sex",
            male: "Male",
            female: "Female",
            promilleNow: "Estimated promille right now",
            promilleSelectedDay: "Peak estimated promille on selected day",
            implausibleEstimate: "Estimate is implausibly high - check amounts and times",
            breathalyzer: "Breathalyzer ≈ {{mg}} mg/L (tolerance applied). Widmark estimate {{promille}}‰.",
            soberSince: "Sober since {{time}}",
            soberEta: "Estimated to be sober at {{time}}",
        },
        timeline: {
            title: "Estimated promille timeline",
            axisLabel: "Estimated promille",
            tooltipLabel: "Estimated promille",
            empty: "No data for {{date}} in this drinking window.",
            soberAt: "Sober at {{time}}",
            soberEta: "Estimated to be sober at {{time}}",
        },
        editModal: {
            title: "Edit drink entry",
            save: "Save",
            drinkLabel: "Select drink",
            drinkPlaceholder: "Select drink",
            amountLabel: "Amount (ml)",
            finishedLabel: "Finished at",
            startedLabel: "Started at",
            durationLabel: "Duration (minutes)",
            durationSuffix: "min",
            chuggedNote: "Marked as chugged.",
            startedAround: "Started around {{time}}",
        },
        messages: {
            loadActivitiesError: "Unable to load drinks.",
            loadAvailableError: "Unable to load drinks list.",
            cannotEditYet: "Cannot edit this entry yet.",
            noDrinksToEdit: "No drinks available to edit.",
            editNoEntry: "No entry selected.",
            editSelectDrink: "Please select a drink.",
            editSelectFinish: "Please choose when the drink was finished.",
            editAmountMin: "Amount must be greater than 0 ml.",
            editDrinkUnavailable: "Selected drink is no longer available.",
            editSuccess: "Entry updated.",
            editError: "Unable to update entry.",
            loginAgain: "Please login again.",
            deleteNotAllowed: "Cannot delete this entry.",
            deleteSuccess: "Entry deleted.",
            deleteError: "Unable to delete entry.",
        },
        drinkOptionLabel: "{{drink}} ({{abv}}% ABV)",
    },
    drinkHistory: {
        summary: { days: "Drinking days", drinks: "Drinks logged", volume: "Total volume" },
        actions: { retry: "Try again" },
        loading: "Loading your drink history",
        authRequired: {
            title: "Login required",
            description: "Sign in to see your drink history calendar.",
        },
        page: {
            title: "Drink history",
            subtitle: "Browse your past drinking days on the calendar.",
        },
        calendar: {
            scrollRegion: "Scrollable drink history calendar",
            yearLabel: "Year",
            monthLabel: "Month",
            monthMode: "Month view",
            yearMode: "Year view",
            badgeTooltip: "{{count}} entries · {{amount}} ml",
        },
        dayPanel: {
            titleWithDate: "Entries for {{date}}",
            emptyDay: "No drinks logged on {{date}}.",
            entryTitle: "{{drink}} - {{amount}} ml",
            totalLabel: "Total: {{amount}} ml",
            queued: "waiting to sync",
            rejected: "not synced",
        },
        empty: {
            title: "No drink history yet",
            description: "Log a drink to start building your history.",
        },
        messages: {
            loadActivitiesError: "Unable to load drink history.",
        },
    },
    recap: {
        authRequired: {
            title: "Login required",
            description: "Sign in to see your private year in review.",
        },
        loading: "Loading your year in review",
        loadError: "Your activities could not be loaded.",
        retry: "Try again",
        page: {
            title: "Year in review",
            subtitle: "A factual summary of your own logged drinks.",
        },
        yearLabel: "Calendar year",
        summary: "In {{year}} you logged {{drinks}} drinks on {{days}} days.",
        privacy: "Only you can see this.",
        empty: "No drinks were logged in {{year}}.",
        hidden: "Your year in review is hidden on this device.",
        hide: "Hide recap",
        show: "Show recap",
        metrics: {
            totalDrinks: "Drinks logged",
            drinkingDays: "Days with drinks logged",
            average: "Average per drinking day",
            busiestMonth: "Month with most drinks",
            mostFrequentDrink: "Most frequently logged drink",
        },
        monthly: {
            title: "Drinks logged by month",
            chartLabel: "Number of drinks logged in each month of {{year}}",
            tableLabel: "View monthly numbers as a table",
            month: "Month",
            drinks: "Drinks logged",
        },
        comparison: {
            title: "Compared with {{year}}",
            neutral: "Difference in drinks logged: {{difference}}",
            noData: "No previous-year data is available.",
        },
    },
    tournaments: {
        noActiveEvent: { title: "No active event", description: "Join or start an event to see its tournaments." },
        loadError: "Could not load event tournaments",
        loading: "Loading event tournaments",
        loadingOne: "Loading tournament",
        retry: "Retry",
        eventNotFound: { title: "Event not found", description: "Choose a valid event to view its tournaments." },
        completed: "Completed",
        inProgress: "In progress",
        eventFallback: "Event {{id}}",
        create: "Create tournament",
        title: "Tournaments",
        discipline: { beerpong: "Beer pong" },
        open: "Open",
        noneNew: "No new tournaments yet.",
        newSection: "New tournaments",
        runningSection: "Running tournaments",
        noneRunning: "No running tournaments.",
        older: "Older tournaments",
        noneCompleted: "No completed tournaments yet.",
        inProgressClassic: "In-progress classic tournaments",
        openClassic: "Open classic manager",
        classic: "Classic",
        noneClassic: "No classic tournaments for this event.",
        signInToCreate: "Sign in and join an event before creating a tournament.",
        signInEmpty: "Sign in and join an event to create its tournament.",
        enterNameEntrants: "Enter a tournament name and at least two named entrants.",
        enterPassword: "Enter a password for this tournament.",
        shareLinkFailed: "Tournament created, but its public link could not be enabled.",
        createError: "Could not create tournament.",
        creating: "Creating tournament…",
        visibility: { label: "Visibility", public: "Public", friends: "Friends", password: "Password protected", private: "Private" },
        passwordLabel: "Password",
        passwordAria: "Tournament password",
        saveResultError: "Could not save result.",
        updateEntrantError: "Could not update entrant.",
        signInToOpen: "Sign in to open this tournament.",
        notFound: "Tournament not found.",
        playerView: "Player view",
        organizerView: "Organizer view",
        liveUnavailable: "Live update unavailable. Retrying…",
        liveUpdated: "Live · updated {{seconds}}s ago",
        liveStaleUpdated: "Stale · updated {{seconds}}s ago",
        yourNext: "Your next match",
        noUpcoming: "No upcoming match is assigned to you.",
        bracket: "Bracket",
        standings: "Standings",
        joinError: "Could not join tournament.",
        join: "Join tournament",
        entrants: "Entrants",
        advanceError: "Could not advance tournament.",
        advancePendingMatches: "Report all pending matches before starting the next stage.",
        advance: "Advance stage",
        shareError: "Could not update share link.",
        revokeShare: "Revoke share link",
        createShare: "Create share link",
    },
};

export default en;
